import { FITNESS_WORKOUT_FINISH_LIMITS as LIMITS } from '../../../supabase/functions/_shared/fitnessWorkoutFinishDomain';
import { readWorkoutPlanDays } from './workoutPreparation';
import { normalizeFreeWorkoutItems, type FreeWorkoutItem } from './freeWorkoutBuilder';

export interface RoutineProgression {
  version: 1; enabled: boolean; repsPerWeek: number; weightKgPerWeek: number; maxWeeks: number;
}
export const DEFAULT_ROUTINE_PROGRESSION: RoutineProgression = { version: 1, enabled: false, repsPerWeek: 1, weightKgPerWeek: 0, maxWeeks: 4 };
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
export function readRoutineProgression(value: unknown): RoutineProgression {
  if (value == null) return { ...DEFAULT_ROUTINE_PROGRESSION };
  if (!record(value) || value.version !== 1 || typeof value.enabled !== 'boolean'
    || !Number.isInteger(value.repsPerWeek) || Number(value.repsPerWeek) < 0 || Number(value.repsPerWeek) > 5
    || typeof value.weightKgPerWeek !== 'number' || !Number.isFinite(value.weightKgPerWeek) || value.weightKgPerWeek < 0 || value.weightKgPerWeek > 20
    || !Number.isInteger(value.maxWeeks) || Number(value.maxWeeks) < 1 || Number(value.maxWeeks) > 12) throw new Error('Invalid routine progression');
  return { version: 1, enabled: value.enabled, repsPerWeek: Number(value.repsPerWeek), weightKgPerWeek: value.weightKgPerWeek, maxWeeks: Number(value.maxWeeks) };
}

/** Every week is an editable proposal. No automatic load increase or completion. */
export function applyRoutineProgression(items: readonly FreeWorkoutItem[], progression: RoutineProgression, week: number): FreeWorkoutItem[] {
  const rule = readRoutineProgression(progression);
  if (!Number.isInteger(week) || week < 1 || week > rule.maxWeeks) throw new Error('Invalid routine week');
  const offset = rule.enabled ? week - 1 : 0;
  return normalizeFreeWorkoutItems(items.map(item => ({ ...item,
    defaultReps: item.timed ? item.defaultReps : Math.min(LIMITS.reps, item.defaultReps + offset * rule.repsPerWeek),
    defaultWeight: offset === 0 ? item.defaultWeight : Math.min(LIMITS.weight, item.defaultWeight + offset * rule.weightKgPerWeek),
    ...(item.timed ? { durationSeconds: item.defaultReps } : {}),
  })));
}

export function proposalFromRoutineDay(value: unknown): FreeWorkoutItem[] {
  const [day] = readWorkoutPlanDays([value]);
  if (!day) return [];
  return normalizeFreeWorkoutItems(day.exercises.map(exercise => ({
    name: exercise.name, muscle: exercise.muscle, sets: exercise.sets, defaultReps: exercise.amount,
    defaultWeight: exercise.weight, timed: exercise.timed, restSeconds: exercise.restSeconds,
    ...(exercise.timed ? { durationSeconds: exercise.amount } : {}),
    ...(exercise.notes !== undefined ? { notes: exercise.notes } : {}),
    ...(exercise.groupId ? { groupId: exercise.groupId, ...(exercise.groupType === undefined ? {} : { groupType: exercise.groupType }) } : {}),
    ...(exercise.setTypes ? { setTypes: exercise.setTypes } : {}),
  })));
}

export function personalRoutineDay(name: string, items: readonly FreeWorkoutItem[], progression?: RoutineProgression) {
  if (!name.trim() || name.trim().length > 160 || /[\p{Cc}\p{Cf}\p{Cs}]/u.test(name)) throw new Error('Invalid routine name');
  const normalized = normalizeFreeWorkoutItems(items.map(item => ({ ...item, ...(item.timed ? { durationSeconds: item.defaultReps } : {}) })));
  if (!normalized.length) throw new Error('Empty routine');
  return { name: name.trim(), exercises: normalized.map(item => ({
    name: item.name, muscle_group: item.muscle, sets: item.sets, reps: item.timed ? String(item.defaultReps) + 's' : String(item.defaultReps),
    weight: item.defaultWeight, rest: item.restSeconds,
    ...(item.notes !== undefined ? { notes: item.notes } : {}),
    ...(item.groupId ? { groupId: item.groupId, ...(item.groupType === undefined ? {} : { groupType: item.groupType }) } : {}),
    ...(item.setTypes ? { setTypes: [...item.setTypes] } : {}),
  })), ...(progression ? { progression: readRoutineProgression(progression) } : {}) };
}

/** A permanent edit changes one selected day while preserving every other day and its metadata. */
export function replaceRoutineDay(days: unknown, index: number, items: readonly FreeWorkoutItem[], progression?: RoutineProgression): unknown[] {
  readWorkoutPlanDays(days);
  if (!Array.isArray(days) || !Number.isInteger(index) || index < 0 || index >= days.length || !record(days[index])) throw new Error('Routine day missing');
  return days.map((day, i) => i === index ? { ...day, ...personalRoutineDay(String(day.name || ''), items, progression) } : day);
}
